/* eslint-disable @typescript-eslint/ban-types */
import React from 'react'

import { BowtieView } from 'plugin-codaf-risk-ext-bowtie/BowtieManagment/BowtieViews'
import {
    IOverviewAlarmEvent,
    IOverviewAsset
} from 'plugin-codaf-risk-ext-bowtie/BowtieManagment/components/AssetSelector/AssetSelector'
import {
    BowtieManagementWidgetConfigs,
    IAlarp,
    IAlarpThreshold,
    IAsset,
    IBarrierStatusSpecialStatus,
    IBowtie
} from 'plugin-codaf-risk-ext-bowtie/types'
import { IAlarmEvent, IAlarmSettings } from 'plugin-codaf-risk-ext-bowtie/types/alarms'

export interface OverviewConfigProps<extraJSONConfig = {}> {
    onChange<K extends keyof LiveWidgetConfig<BowtieManagementWidgetConfigs & extraJSONConfig>>(
        key: K,
        value: LiveWidgetConfig<BowtieManagementWidgetConfigs & extraJSONConfig>[K]
    ): void
    onChangeLayoutConfig<K extends keyof LiveWidgetConfig<BowtieManagementWidgetConfigs & extraJSONConfig>>(
        key: K,
        value: LiveWidgetConfig<BowtieManagementWidgetConfigs & extraJSONConfig>[K]
    ): void
    config: LiveWidgetConfig<BowtieManagementWidgetConfigs & extraJSONConfig>
    widgetConfig: Readonly<BowtieManagementWidgetConfigs & extraJSONConfig>
    assets: IAsset[]
}

export interface OverviewProps<extraJSONConfig = {}> {
    bowtiesView: BowtieView[]
    setSelectedBowtie: React.Dispatch<React.SetStateAction<number>>
    alarpList: IAlarp[]
    width: number
    height: number
    widget: LiveWidget<BowtieManagementWidgetConfigs & extraJSONConfig>
    setShowAlarmsDashboard: React.Dispatch<React.SetStateAction<boolean>>
    alarms: Map<number, IAlarmEvent>
    setAssetToAlarm: (k: IOverviewAsset) => void
    assetToAlarm: IOverviewAsset
    alarmData: IOverviewAlarmEvent
    setAlarmData: (k: IOverviewAlarmEvent) => void
    alarmBowties: IBowtie[]
    barrierStatusList: IBarrierStatusSpecialStatus[]
    userData: IAlarmSettings
}

export interface OverviewItem {
    uid: string
    name: string
    /**
     * Hide asset selector in widget config
     *
     * @default true
     */
    hideDefaultResourcesToDisplaySelect?: boolean
    /**
     * Define if this overview will be show all Resources, property will be ignored if hideDefaultResourcesToDisplaySelect = true
     *
     * @default true
     * */
    selectAllResourcesToDisplay?: boolean
    /**
     * Define if this overview is new default
     *
     * @default true
     * */
    isDefault?: boolean
    /**
     * Hide overview selector in widget config, property will be ignored if isDefault = false
     *
     *  @default true
     */
    hideOverviewSelector?: boolean
    element: React.ComponentType<OverviewProps>
    config?: LiveWidgetConfigForm
}

class OverviewDisplayAPI {
    private defaultOverviewUID: string

    private repository: Map<string, OverviewItem>

    constructor() {
        this.repository = new Map()
    }

    public getDefaultOverview(): OverviewItem {
        return this.repository.get(this.defaultOverviewUID)
    }

    public register(item: OverviewItem): void {
        const fixedUID = (item.uid ?? '').toLowerCase().trim()

        if (this.repository.has(fixedUID)) {
            throw new Error('Already existis a overview with this uid')
        }

        const normalizedOptions: OverviewItem = {
            ...item,
            hideDefaultResourcesToDisplaySelect: item.hideDefaultResourcesToDisplaySelect ?? true,
            selectAllResourcesToDisplay: item.selectAllResourcesToDisplay ?? true,
            hideOverviewSelector: true, // item.hideOverviewSelector ?? true,
            isDefault: true, // item.isDefault ?? true,
            uid: fixedUID
        }

        this.repository.set(fixedUID, normalizedOptions)

        // Null check use "==" because in this case will check null and undefined
        if (normalizedOptions.isDefault == null || normalizedOptions.isDefault === true) {
            this.defaultOverviewUID = fixedUID
        }
    }

    public unregister(uid: string): void {
        const fixedUID = (uid ?? '').toLowerCase().trim()

        if (fixedUID.length === 0 || !this.repository.has(fixedUID)) {
            throw new Error('Overview not found')
        }

        if (fixedUID === 'default') {
            throw new Error('Is not possible to remove default overview')
        }

        this.repository.delete(uid)
    }
}

export const overviewDisplayAPI = new OverviewDisplayAPI()
